import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';
import type { ButtonProps } from '@/app/components/button';
import { Button } from '@/app/components/button';
import { Icon } from '@/app/components/icons';
import { IconChevronRight } from '@/app/components/icons/ui-actions/icon-chevron-right';
import { Container } from '@/app/components/layouts/container';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, type DataGAProps } from '@/app/utils/createDataGA';
const BreadcrumbStyled = cnva('relative flex items-center overflow-hidden py-2 whitespace-nowrap before:pointer-events-none before:absolute before:inset-0 before:border-b-2 md:py-4', {
  variants: {
    theme: {
      light: 'before:border-gray-50',
      dark: 'before:border-gray-800'
    }
  },
  defaultVariants: {
    theme: 'light'
  }
});
export interface BreadcrumbItem {
  label?: string;
  href?: string;
}
export interface BreadcrumbProps extends VariantProps<typeof BreadcrumbStyled> {
  /**
   * Array of breadcrumb item with label and href
   */
  links: BreadcrumbItem[];
  /**
   * Active link for last item, default is false
   */
  activeLast?: boolean;
  /**
   * (Optional) class name for the container element.
   */
  className?: string;

  /**
   * (Optional) The tracking data for the component.
   */
  tracking?: Omit<DataGAProps, 'click'>;

  /**
   * Emit the BreadcrumbList JSON-LD. Defaults to true. Set to false when the
   * breadcrumb is already modeled inside a page-level `@graph` to avoid a
   * duplicate BreadcrumbList.
   */
  jsonLd?: boolean;
}
export const Breadcrumb = t0 => {
  const $ = _c(40);
  const {
    links: t1,
    theme: t2,
    activeLast: t3,
    className,
    tracking,
    jsonLd: t4
  } = t0;
  let activeLast;
  let buttonProps;
  let links;
  let t5;
  let t6;
  let theme;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      links = t1 === undefined ? [] : t1;
      theme = t2 === undefined ? "light" : t2;
      activeLast = t3 === undefined ? false : t3;
      const jsonLd = t4 === undefined ? true : t4;
      if (links.length <= 1) {
        t6 = null;
        break bb0;
      }
      const t7 = theme === "light" ? "black" : "white";
      let t8;
      if ($[10] !== t7) {
        t8 = {
          variant: "tertiary",
          width: "auto",
          color: t7,
          size: "xs"
        };
        $[10] = t7;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      buttonProps = t8;
      const JSON_LD = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: links.map(_temp) as ListItem[]
      };
      t5 = !!jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(JSON_LD)
      }} />;
    }
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = activeLast;
    $[5] = buttonProps;
    $[6] = links;
    $[7] = t5;
    $[8] = t6;
    $[9] = theme;
  } else {
    activeLast = $[4];
    buttonProps = $[5];
    links = $[6];
    t5 = $[7];
    t6 = $[8];
    theme = $[9];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = createDataAttributes({
      test: "breadcrumb"
    });
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== theme) {
    t8 = BreadcrumbStyled({
      theme
    });
    $[13] = theme;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== activeLast || $[16] !== buttonProps || $[17] !== className || $[18] !== links || $[19] !== theme || $[20] !== tracking?.eventName || $[21] !== tracking?.group) {
    let t10;
    if ($[23] !== activeLast || $[24] !== buttonProps || $[25] !== className || $[26] !== links.length || $[27] !== theme || $[28] !== tracking?.eventName || $[29] !== tracking?.group) {
      t10 = (link_0, index_0) => {
        const isMiddleItems = index_0 > 0 && index_0 < links.length - 1 && links.length > 2;
        const isLastItem = index_0 === links.length - 1;
        const isActiveLast = isLastItem && !activeLast;
        return <li key={index_0} className={cn("inline-flex items-center", {
          "overflow-hidden": isLastItem
        })} {...createDataAttributes({
          test: `breadcrumb-label-${index_0}`
        })}><Button {...buttonProps} href={link_0.href} as={isActiveLast ? "span" : undefined} className={cn("block", {
            "text-gray-900": theme === "light",
            "pointer-events-none truncate": isActiveLast,
            "hidden md:block": isMiddleItems
          }, !isLastItem && className)} {...createDataGA({
            eventName: tracking?.eventName ?? "page-click",
            group: tracking?.group ?? undefined,
            click: true,
            text: link_0?.label,
            url: link_0?.href
          })}>{link_0.label}</Button>{!!isMiddleItems && <Button {...buttonProps} aria-hidden={true} role="presentation" as="span" className={cn("pointer-events-none md:hidden", {
            "text-gray-900": theme === "light"
          }, className)}>...</Button>}{!isLastItem && <Icon icon={<IconChevronRight />} className={cn({
            "text-gray-800": theme === "light",
            "text-gray-50": theme === "dark"
          })} />}</li>;
      };
      $[23] = activeLast;
      $[24] = buttonProps;
      $[25] = className;
      $[26] = links.length;
      $[27] = theme;
      $[28] = tracking?.eventName;
      $[29] = tracking?.group;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t9 = links.map(t10);
    $[15] = activeLast;
    $[16] = buttonProps;
    $[17] = className;
    $[18] = links;
    $[19] = theme;
    $[20] = tracking?.eventName;
    $[21] = tracking?.group;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[31] !== t8 || $[32] !== t9) {
    t10 = <Container><ol className={t8}>{t9}</ol></Container>;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10 || $[35] !== t7) {
    t11 = <nav {...t7} aria-label="breadcrumb">{t10}</nav>;
    $[34] = t10;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t11 || $[38] !== t5) {
    t12 = <>{t5}{t11}</>;
    $[37] = t11;
    $[38] = t5;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
};
function _temp(link, index) {
  return {
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@id": link.href ? `${process.env.HOST}${link.href}` : undefined,
      name: link.label
    }
  };
}